/* eslint-disable import/no-extraneous-dependencies */
import { ApolloClient, InMemoryCache, ApolloLink, ApolloProvider } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { ReactNode } from 'react';
import { TOKEN_KEY } from './auth/AuthProvider';

interface AuthorizedApolloProviderProps {
  children: ReactNode;
}

const AuthorizedApolloProvider = ({ children }: AuthorizedApolloProviderProps) => {
  const authMiddleware = setContext(async (_, { headers, ...context }) => {
    const token = localStorage.getItem(TOKEN_KEY);

    // Get the operation name to determine if this is an auth mutation
    const operationName = context.operationName;
    const isAuthMutation = ['Login', 'Signup', 'RefreshToken', 'RequestPasswordReset', 'ResetPassword', 'VerifyEmail', 'ResendVerificationEmail', 'AcceptInvitation', 'ResendInvitation'].includes(operationName);

    // Don't send Authorization header for auth mutations to avoid 400 errors with expired tokens
    const shouldIncludeAuth = token && !isAuthMutation;

    if (shouldIncludeAuth && process.env.REACT_APP_ENV == 'development') {
      console.log(
        'PLAYGROUND_HEADER:',
        JSON.stringify({ Authorization: `Bearer ${token}` }),
      );
    }

    return {
      ...context,
      headers: {
        ...headers,
        ...(shouldIncludeAuth ? { Authorization: `Bearer ${token}` } : {}),
      },
    };
  });

  const client = new ApolloClient({
    cache: new InMemoryCache(),
    link: ApolloLink.from([
      onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
          graphQLErrors.forEach(({ message, locations, path }) =>
            console.error(
              `[GraphQL error]: Message: ${message}, Location: ${locations?.[0]?.line}, Path: ${path}`,
            ),
          );
        }
        if (networkError) {
          console.error('[Network error]:', networkError);
        }
      }),
      authMiddleware,
      new RetryLink(),
      new BatchHttpLink({ uri: '/graphql' }),

    ]),
  });

  return <ApolloProvider client={client}> {children} </ApolloProvider>;
};

export default AuthorizedApolloProvider;